import type { AudienceFilters } from '@brushfire/types'
import type {
  ISignalProviderListCard,
  ISignalProviderListDemographics,
  SignalProviderVerifiedStatus,
} from '@brushfire/types'

export type SignalProviderVerifiedFilter = 'all' | SignalProviderVerifiedStatus

export function matchesTextSearch(card: ISignalProviderListCard, search: string): boolean {
  const q = search.trim().toLowerCase()
  if (!q) return true

  const haystack = [
    card.signalProviderId,
    card.firstName,
    card.lastName,
    card.email,
    card.phone,
    `${card.firstName} ${card.lastName}`.trim(),
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase())

  return haystack.some((value) => value.includes(q))
}

function ageFromDemographics(demo: ISignalProviderListDemographics): number | null {
  if (demo.age != null) return demo.age
  if (!demo.dateOfBirth) return null
  const birth = new Date(demo.dateOfBirth)
  if (Number.isNaN(birth.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDelta = today.getMonth() - birth.getMonth()
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) {
    age -= 1
  }
  return age
}

function matchesDeviceType(
  deviceTypes: ISignalProviderListDemographics['deviceTypes'],
  filterChannels: NonNullable<AudienceFilters['deviceType']>,
): boolean {
  if (!filterChannels.length) return true
  if (!deviceTypes?.length) return true
  if (filterChannels.length === 1) {
    return deviceTypes.includes(filterChannels[0])
  }
  return filterChannels.every((ch) => deviceTypes.includes(ch))
}

function matchesDemographics(
  demo: ISignalProviderListDemographics,
  filters: AudienceFilters,
): boolean {
  const { min, max } = filters.ageRange ?? {}
  if (min != null || max != null) {
    const age = ageFromDemographics(demo)
    if (age == null) return false
    if (min != null && age < min) return false
    if (max != null && age > max) return false
  }

  if (filters.gender?.length && (!demo.gender || !filters.gender.includes(demo.gender))) {
    return false
  }

  if (filters.location?.state) {
    const state = demo.location?.state ?? null
    if (state !== filters.location.state) return false
  }
  if (filters.location?.city) {
    const city = demo.location?.city ?? null
    if (city !== filters.location.city) return false
  }

  if (filters.education?.length && (!demo.education || !filters.education.includes(demo.education))) {
    return false
  }
  if (
    filters.employment?.length &&
    (!demo.employment || !filters.employment.includes(demo.employment))
  ) {
    return false
  }
  if (filters.deviceType?.length && !matchesDeviceType(demo.deviceTypes, filters.deviceType)) {
    return false
  }
  if (
    filters.maritalStatus?.length &&
    (!demo.maritalStatus || !filters.maritalStatus.includes(demo.maritalStatus))
  ) {
    return false
  }
  if (
    filters.householdSize?.length &&
    (!demo.householdSize || !filters.householdSize.includes(demo.householdSize))
  ) {
    return false
  }
  if (
    filters.numberOfChildren?.length &&
    (!demo.numberOfChildren || !filters.numberOfChildren.includes(demo.numberOfChildren))
  ) {
    return false
  }
  if (filters.industry?.length && (!demo.industry || !filters.industry.includes(demo.industry))) {
    return false
  }
  if (
    filters.housingStatus?.length &&
    (!demo.housingStatus || !filters.housingStatus.includes(demo.housingStatus))
  ) {
    return false
  }

  return true
}

export function filterSignalProviderCards(
  items: ISignalProviderListCard[],
  search: string,
  verifiedFilter: SignalProviderVerifiedFilter,
  demographicFilters: AudienceFilters,
): ISignalProviderListCard[] {
  return items.filter((card) => {
    if (!matchesTextSearch(card, search)) return false
    if (verifiedFilter !== 'all' && card.verifiedStatus !== verifiedFilter) return false
    if (!matchesDemographics(card.demographics, demographicFilters)) return false
    return true
  })
}

export function applyVerifiedQuickStatFilter(
  _current: SignalProviderVerifiedFilter,
  pill: SignalProviderVerifiedStatus,
): SignalProviderVerifiedFilter {
  return pill
}

export function hasActiveDemographicFilters(filters: AudienceFilters): boolean {
  return Object.keys(filters).length > 0
}
